import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Buildings } from "@phosphor-icons/react";
import { toast } from "sonner";

const fmtMoney = (n) => "$" + (n || 0).toLocaleString("en-US");
const empty = { name: "", number: "", brand: "Holiday Inn Express", address: "", estimated_budget: "", start_date: "", completion_date: "", description: "" };

export default function Projects() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const canCreate = user?.role === "owner" || user?.role === "project_manager";

  const load = () => api.get("/projects").then(({ data }) => setProjects(data.projects)).catch(() => {});
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form, estimated_budget: parseFloat(form.estimated_budget) || 0 };
      const { data } = await api.post("/projects", payload);
      toast.success("Project created");
      setOpen(false);
      setForm(empty);
      await load();
      navigate(`/projects/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-6 lg:p-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="overline text-primary mb-1">Phase 1 · Ownership Portal</div>
          <h1 className="font-heading font-black text-3xl tracking-tighter">Projects</h1>
        </div>
        {canCreate && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-project-button" className="rounded-none bg-primary hover:bg-primary/90 text-white gap-2 hover:-translate-y-[1px] transition-transform duration-150">
                <Plus size={18} weight="bold" /> New Project
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-none max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-heading font-black tracking-tight">Project Information</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4" data-testid="project-form">
                <F label="Project Name" req><Input value={form.name} onChange={set("name")} required data-testid="project-name-input" className="rounded-none h-11" /></F>
                <F label="Project Number"><Input value={form.number} onChange={set("number")} data-testid="project-number-input" className="rounded-none h-11" /></F>
                <F label="Brand"><Input value={form.brand} onChange={set("brand")} data-testid="project-brand-input" className="rounded-none h-11" /></F>
                <F label="Estimated Budget"><Input type="number" value={form.estimated_budget} onChange={set("estimated_budget")} data-testid="project-budget-input" className="rounded-none h-11" placeholder="0" /></F>
                <F label="Project Address" full><Input value={form.address} onChange={set("address")} data-testid="project-address-input" className="rounded-none h-11" /></F>
                <F label="Start Date"><Input type="date" value={form.start_date} onChange={set("start_date")} data-testid="project-start-input" className="rounded-none h-11" /></F>
                <F label="Completion Date"><Input type="date" value={form.completion_date} onChange={set("completion_date")} data-testid="project-completion-input" className="rounded-none h-11" /></F>
                <F label="Description" full><Textarea value={form.description} onChange={set("description")} data-testid="project-description-input" className="rounded-none" rows={3} /></F>
                <div className="sm:col-span-2">
                  <Button type="submit" disabled={busy} data-testid="project-submit-button" className="w-full rounded-none h-11 bg-primary hover:bg-primary/90 text-white">
                    {busy ? "Creating…" : "Create Project"}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {projects.length ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {projects.map((p) => (
            <button key={p.id} onClick={() => navigate(`/projects/${p.id}`)}
              data-testid={`project-card-${p.id}`}
              className="text-left bg-card border border-border p-6 hover:border-primary transition-colors duration-150 group">
              <div className="flex items-start justify-between">
                <div className="overline text-primary">{p.brand}</div>
                <span className="text-xs px-2 py-1 bg-secondary">{p.status}</span>
              </div>
              <div className="font-heading font-bold text-xl tracking-tight mt-3 group-hover:text-primary transition-colors duration-150">{p.name}</div>
              <div className="text-sm text-muted-foreground mt-1">{p.address || "No address set"}</div>
              <div className="grid grid-cols-3 gap-2 mt-5 pt-5 border-t border-border text-center">
                <Stat n={p.team_count} l="Team" />
                <Stat n={p.document_count} l="Docs" />
                <Stat n={p.gc_count} l="GCs" />
              </div>
              <div className="mt-4 font-heading font-bold">{fmtMoney(p.estimated_budget)}</div>
            </button>
          ))}
        </div>
      ) : (
        <div className="bg-card border border-border border-dashed p-16 text-center">
          <Buildings size={40} className="mx-auto text-muted-foreground mb-4" weight="duotone" />
          <p className="text-muted-foreground">No projects yet.{canCreate ? " Create your first one above." : " You'll see projects here once you're added to a team."}</p>
        </div>
      )}
    </div>
  );
}

const Stat = ({ n, l }) => (
  <div><div className="font-heading font-bold text-lg">{n ?? 0}</div><div className="overline text-muted-foreground !text-[0.6rem]">{l}</div></div>
);
const F = ({ label, req, full, children }) => (
  <div className={`space-y-2 ${full ? "sm:col-span-2" : ""}`}>
    <Label className="overline">{label}{req && <span className="text-primary ml-1">*</span>}</Label>
    {children}
  </div>
);
